/**
 * Helper methods for Floats
 */
import { round, fix, floor, ceil } from 'mathjs'
import { Maybe, curry, gt, isNaN, match, notEquals, replace, toString as toString_ } from '~/utils/lib/core/prelude'

const ERRORS = {
  NOT_A_FLOATABLE_VALUE: 'not_a_floatable_value',
  MAXIMUM_DECIMAL_COUNT_REACHED: 'maximum_decimal_count_reached',
}

/**
 * @name cast
 * @throws {Error}
 * @type {import('./float_').curry_cast}
 */
export const cast = curry((
  /** @type {import('./float_').TRUNCATE_OPERATION} */ operation,
  /** @type {number} */ decimalCount,
  /** @type {any} */ value
) => {
  const castToString = (() => {
    switch (typeof value) {
      case 'string': return value
      // due to some browser directly outputing `number` somewhat
      case 'number': return toString_(value)
      // obscure cases: maybe from a copy-paste
      default: throw new Error(ERRORS.NOT_A_FLOATABLE_VALUE)
    }
  })()

  const normaliseCommaSeparation = replace(/,|、|،|⹁|﹐/g, '.', castToString)
  const normaliseSpaceSeparation = replace(/\s/g, ' ', normaliseCommaSeparation)

  if (normaliseSpaceSeparation.length === 0)
    throw new Error(ERRORS.NOT_A_FLOATABLE_VALUE)

  const containsUnwantedChars = notEquals(
      match(/[ eE\d.+-]/g, normaliseSpaceSeparation).join('').length,
      castToString.length
    )

  if (containsUnwantedChars)
    throw new Error(ERRORS.NOT_A_FLOATABLE_VALUE)

  const strictCharFilter = match(/[eE\d.+-]/g, normaliseSpaceSeparation).join('')
  const castToFloat = parseFloat(strictCharFilter)

  if (isNaN(castToFloat))
    throw new Error(ERRORS.NOT_A_FLOATABLE_VALUE)

  switch (operation) {
    case 'round': return round(castToFloat, decimalCount)
    case 'ceil': return ceil(castToFloat, decimalCount)
    case 'fix': return fix(castToFloat, decimalCount)
    case 'floor': return floor(castToFloat, decimalCount)
  }
})
/**
 * @name mCast
 * @desc Derived `fromString` with `Maybe` monad
 * @type {import('./float_').curry_mCast}
 */
export const mCast = curry((
  operation,
  decimalCount,
  value
  ) => {
  try {
    const float = cast(operation,decimalCount, value)

    return Maybe.Just(float)

  } catch (_) {
    return Maybe.Nothing()
  }
})

export const round_1decimal   = cast('round', 1)
export const round_2decimals  = cast('round', 2)
export const round_12decimals = cast('round', 12)

/**
 * @name getDecimalCount
 * @example
 *
 *    getDecimalCount(2)      // `0`
 *    getDecimalCount(2.00)   // `0`
 *    getDecimalCount(5.33)   // `2`
 *
 * @param {number} value
 * @link https://stackoverflow.com/a/17369384/6003907
 * @returns {number}
 */
export const getDecimalCount = value => {
  if ( (value % 1) === 0)
    return 0

  const splitDec = value.toString().split('.')

  return splitDec[1] ?
    splitDec[1].length :
    0
}
/**
 * @name respectDecimalCount
 * @example
 *
 *    respectDecimalCount(2, 1)     // `{void}`
 *    respectDecimalCount(2, 0.33)  // `{void}`
 *    respectDecimalCount(2, 3.333) // `Error::ERRORS.MAXIMUM_DECIMAL_COUNT_REACHED`
 *
 * @throws {Error}
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(maximumDecimalCount: number, value: number) => number>}
 */
export const respectDecimalCount = curry((
  maximumDecimalCount,
  value
) => {
  const decimalCount = getDecimalCount(value)

  if (gt(decimalCount, maximumDecimalCount))
    throw new Error(ERRORS.MAXIMUM_DECIMAL_COUNT_REACHED)
})

export const respect_2decimalCount  = respectDecimalCount(2)
export const respect_12decimalCount = respectDecimalCount(12)

/**
 * @function toString
 * @example
 *    toString(300000)     // "300 000"
 *    toString('1234.56')  // "1 234,56"
 *    toString('1 234,56') // "1 234,56"
 *    toString(['foo'])    // ""
 *    toString(null)       // ""
 *
 * @param {any} value
 * @param {Intl.NumberFormatOptions} [options={}]
 * @return {string}
 */
export function toString(value, options = {}) {
  switch (true) {
    case typeof value === 'number':
      return value.toLocaleString('fr-FR', options)

    case typeof value === 'string': {
      const normaliseCommaSeparation = replace(/,|、|،|⹁|﹐/g, '.', value)
      const normaliseSpaceSeparation = replace(/ |'/g, '', normaliseCommaSeparation)
      const castToFloat = parseFloat(normaliseSpaceSeparation)

      if (isNaN(castToFloat))
        return ''

      return /** @type {number} */ (castToFloat).toLocaleString('fr-FR', options)
    }

    default:
      return ''
  }
}
